import { forEach, omit, isEmpty, get, reduce, map, filter, defaults } from 'underscore';
import { isEmptyUrl } from '@bingads-webui-campaign-react/url-input-fluent';
import { newVideoFromMTObject } from '@bingads-webui-campaign-react/image-experience-video-part';
import { headlineFields, longHeadlineFields, descriptionFields } from './constants';

/**
 * The optional parameters that are set to empty will be set to 'delete_value' during update,
 * need to remove these params before feed to model on edit UI, otherwise the string 'delete_value'
 * will be shown on UI instead of empty value.
 *
 * @param {object} params The object returned from the update request.
 *
 * @returns {object} Updated object to be saved for edit on UI later.
 *
 */
export function omitDeletedParameters(params) {
  const optionalParameters = [
    'MobileFinalUrls',
    'FinalUrlSuffix',
    'TrackingUrlTemplate',
    'UrlCustomParameters',
    'LandscapeLogoMediaId',
    'SquareLogoMediaId',
  ];

  let formattedParams = params;

  forEach(optionalParameters, (p) => {
    if (formattedParams[p] === 'delete_value') {
      formattedParams = omit(formattedParams, p);
    }
  });

  return formattedParams;
}

export function convertToLowerCaseKey(objs) {
  return objs.map((obj) => {
    const item = {};
    Object.keys(obj).forEach((key) => {
      item[key.charAt(0).toLowerCase() + key.substring(1)] = obj[key];
    });

    return item;
  });
}

export function convertFromAdModel(ad, processImages = true) {
  if (!Array.isArray(ad.Headlines)) {
    return ad;
  }

  const headlines = reduce(get(ad, ['Headlines'], []), (memo, val, idx) => {
    const results = { ...memo };
    results[`Headline${idx}`] = isEmpty(val.Asset) ? val.Text : val.Asset.Text;
    return results;
  }, {});

  const longHeadlines = reduce(get(ad, ['LongHeadlines'], []), (memo, val, idx) => {
    const results = { ...memo };
    results[`LongHeadline${idx}`] = isEmpty(val.Asset) ? val.Text : val.Asset.Text;
    return results;
  }, {});

  const descriptions = reduce(get(ad, ['Descriptions'], []), (memo, val, idx) => {
    const results = { ...memo };
    results[`Description${idx}`] = isEmpty(val.Asset) ? val.Text : val.Asset.Text;
    return results;
  }, {});

  const CallToAction = ad && ad.CallToAction ? {
    CallToAction: {
      cta: ad.CallToAction,
      language: ad.CallToActionLanguage,
    },
  } : {};

  const adModel = {
    ...ad,
    Headlines: headlines,
    LongHeadlines: longHeadlines,
    Descriptions: descriptions,
    ...CallToAction,
  };

  if (ad && ad.Videos) {
    adModel.videos = map(ad.Videos, video => newVideoFromMTObject(video.Asset));
  }

  if (processImages) {
    adModel.Images = isEmpty(ad.ImageList) ? {
      ImageAssets: ad.Images,
    } : {
      ImageList: ad.ImageList,
    };
  }

  return adModel;
}

export function convertFromBulkResponse(ad = {}) {
  const Headlines = JSON.parse(get(ad, ['Headlines'], '[]'));
  const LongHeadlines = JSON.parse(get(ad, ['LongHeadlines'], '[]'));
  const Descriptions = JSON.parse(get(ad, ['Descriptions'], '[]'));
  const Images = JSON.parse(get(ad, ['Images'], '[]'));

  return convertFromAdModel({
    ...ad,
    Headlines,
    LongHeadlines,
    Descriptions,
    Images,
  }, false);
}

const mapAssets = (fields, viewModel) => JSON.stringify(map(filter(
  fields,
  field => !isEmpty(viewModel[field])
), field => ({ Text: viewModel[field] })));

export function convertToAdModel(viewModel) {
  const data = {
    ...viewModel,
    Headlines: mapAssets(headlineFields, viewModel.Headlines),
    LongHeadlines: mapAssets(longHeadlineFields, viewModel.LongHeadlines),
    Descriptions: mapAssets(descriptionFields, viewModel.Descriptions),
    CallToAction: viewModel.CallToAction.cta,
    CallToActionLanguage: viewModel.CallToAction.language,
    UrlCustomParameters: filter(viewModel.UrlCustomParameters, param => !isEmpty(param.Key)),
    MobileFinalUrls: isEmptyUrl(viewModel.MobileFinalUrls) ? [] : viewModel.MobileFinalUrls,
  };

  return omit(data, isEmpty);
}

export function convertToAdModelInResponse(ad = {}) {
  const Headlines = map(get(ad, ['Headlines'], {}), headline => ({ Asset: { Text: headline } }));
  const LongHeadlines = map(get(ad, ['LongHeadlines'], {}), longHeadline => ({ Asset: { Text: longHeadline } }));
  const Descriptions = map(get(ad, ['Descriptions'], {}), description => ({ Asset: { Text: description } }));
  const CallToAction = get(ad, ['CallToAction', 'cta']);
  const CallToActionLanguage = get(ad, ['CallToAction', 'language']);
  const Images = get(ad, ['Images', 'ImageAssets'], []);
  const ImageList = get(ad, ['Images', 'ImageList'], []);

  return {
    ...ad,
    Headlines,
    LongHeadlines,
    Descriptions,
    CallToAction,
    CallToActionLanguage,
    Images,
    ImageList,
  };
}

function deleteUndefinedParameters(params) {
  const optionalParameters = ['MobileFinalUrls', 'TrackingUrlTemplate', 'UrlCustomParameters', 'LandscapeLogoMediaId', 'SquareLogoMediaId', 'FinalUrlSuffix', 'Headline', 'LongHeadline', 'Text'];

  forEach(optionalParameters, (p) => {
    if (isEmpty(params[p]) || (params[p] instanceof Array && params[p].length === 0)) {
      // eslint-disable-next-line no-param-reassign
      params[p] = 'delete_value';
    }
  });

  return params;
}

export function processViewModelDataForSaving(viewModel, campaignId, adGroupId, adId) {
  const adData = defaults(convertToAdModel(viewModel), {
    Campaign: {
      Id: Number(campaignId),
    },
    AdGroup: {
      Id: Number(adGroupId),
    },
  });

  if (adId) {
    adData.Id = adId;
    return deleteUndefinedParameters(adData);
  }

  return adData;
}
